<template>
  <div class="reset-password-page">
    <v-container class="my-3" fluid>
      <v-row justify="center">
        <v-col cols="12" md="6">
          <v-card class="pa-4" elevation="7" rounded="lg">
            <v-card-title class="text-center font-weight-bold">
              รีเซ็ตรหัสผ่านใหม่
            </v-card-title>
            <v-card-text class="mt-4">
              <div v-if="loading">
                <v-progress-linear
                  indeterminate
                  class="bg-pink-lighten-2"
                ></v-progress-linear>
                <p class="text-center mt-3">กำลังตรวจสอบลิงก์...</p>
              </div>

              <div v-else-if="tokenError">
                <v-alert type="error" closable class="mb-4">
                  {{ tokenError }}
                </v-alert>
                <p class="text-center">
                  ลิงก์หมดอายุหรือไม่ถูกต้อง
                  กรุณากลับไปที่หน้าเข้าสู่ระบบและร้องขอรหัสผ่านใหม่อีกครั้ง
                </p>
              </div>

              <v-form v-else @submit.prevent="handlePasswordReset">
                <p class="text-center mb-4 text-medium-emphasis">
                  กรุณากำหนดรหัสผ่านใหม่
                </p>

                <v-text-field
                  label="รหัสผ่านใหม่"
                  :type="showNewPassword ? 'text' : 'password'"
                  @click:append-inner="showNewPassword = !showNewPassword"
                  :append-inner-icon="
                    showNewPassword ? 'mdi-eye' : 'mdi-eye-off'
                  "
                  variant="outlined"
                  v-model="newPassword"
                  class="mb-3 bg-pink-lighten-2"
                  autocomplete="new-password"
                  required
                ></v-text-field>

                <v-text-field
                  label="ยืนยันรหัสผ่านใหม่"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  @click:append-inner="
                    showConfirmPassword = !showConfirmPassword
                  "
                  :append-inner-icon="
                    showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'
                  "
                  variant="outlined"
                  v-model="confirmPassword"
                  :rules="[(v) => v === newPassword || 'รหัสผ่านไม่ตรงกัน']"
                  class="bg-pink-lighten-2"
                  autocomplete="new-password"
                  required
                ></v-text-field>

                <v-card-actions class="justify-center mt-4">
                  <v-btn
                    type="submit"
                    variant="elevated"
                    class="bg-pink-lighten-2"
                    :disabled="!isFormValid"
                    :loading="submitting"
                  >
                    ตั้งรหัสผ่านใหม่
                  </v-btn>
                </v-card-actions>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import { getCurrentInstance } from "vue";
const { proxy } = getCurrentInstance();
// Import your API client (e.g., axios) here
// import axios from 'axios';

const route = useRoute();
const router = useRouter();

// Component State
const token = ref("");
const newPassword = ref("");
const confirmPassword = ref("");
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);
const submitting = ref(false);
const loading = ref(true);
const tokenError = ref("");

// Computed Property to validate form
const isFormValid = computed(() => {
  return (
    newPassword.value &&
    confirmPassword.value &&
    newPassword.value === confirmPassword.value &&
    token.value
  ); // Must have a token to submit!
});

// 1. Get Token from URL when page loads
onMounted(() => {
  console.log("Route query params:", route.query);
  const urlToken = route.query.token;
  if (urlToken) {
    token.value = urlToken;
    // (Optional: You could run a quick backend check here to see if the token exists/is valid)
    loading.value = false;
  } else {
    // No token in the URL is a failure
    tokenError.value = "ไม่พบลิงก์รีเซ็ตรหัสผ่าน";
    loading.value = false;
  }
});

// 2. API Call to Reset Password (Step 4 Logic)
async function handlePasswordReset() {
  if (!isFormValid.value) return;

  submitting.value = true;

  try {
    // --- THIS IS THE CRITICAL API CALL ---
    const response = await axios.post("/api/set-password", {
      token: token.value, // Send the token found in the URL
      newPassword: newPassword.value,
      // NOTE: The backend will handle hashing the newPassword and validating the token.
    });

    if (response.data.success) {
      await proxy.$swal({
        title: "",
        text: `รีเซ็ตรหัสผ่านสำเร็จ!`,
        icon: "success",
        showConfirmButton: false,
        timer: 3000,
      });
      router.push("/"); // Redirect to login page
    } else {
      // Handle server-side errors (e.g., token expired, token already used)
      tokenError.value = response.data.message || "เกิดข้อผิดพลาดในการรีเซ็ต";
    }
  } catch (error) {
    console.error(error);
    tokenError.value = "เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์";
  } finally {
    submitting.value = false;
  }
}
</script>
